// src/com/auto_plan_bind_group/AutoPlanBindGroup.js
import React, { useEffect, useRef, useState } from 'react';
//import { styled } from '@mui/material/styles';
import { IconButton, CircularProgress } from '@mui/material';
import CachedIcon from '@mui/icons-material/Cached'; // refresh
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import EditIcon from '@mui/icons-material/Edit';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client';
import { useSnackbar } from 'notistack';
import SearchBox from '../../common/SearchBox';
import {
    ContentConainer, ContentHeader, ControlBox, ToolBox
} from '../../common/TopViewControl';
import { useConfirmDialog } from '../../common/AppConfirmDialog';
import { AppObject, AppStyle, AppWord } from '../../value/AppConst';
import { authTableTreeRepo, autoPlanBindGroupListRepo } from '../../value/AppModel';
import {
    ADD_AUTO_PLAN_BIND_GROUP, EDIT_AUTO_PLAN_BIND_GROUP, REMOVE_AUTO_PLAN_BIND_GROUP
} from './AutoPlanBindGroupGql';
import {
    LIST_AUTO_PLAN_BIND_STORE_SET, makeAutoPlanBindGroupSet,
    // ADD_AUTO_PLAN_BIND_STORE_SET
 } from '../auto_plan_bind_store_set/AutoPlanBindStoreSetGql';
import { AutoPlanBindGroupDictionary } from './AutoPlanBindGroupDict';
import { useAlertSetter, useAuthorizationCheck } from '../../api/app-hook';
import FlexyTable from '../../common/flexytable/FlexyTable';
import AutoPlanBindGroupInput from './AutoPlanBindGroupInput';

function filterBindGroup(item, search) {
    if(search) {
        const searchText = search.trim();
        if(searchText.length===0) return true;
        if(item.dcCd === searchText) return true;
        if(item.vehTypeName && item.vehTypeName.indexOf(searchText) >= 0) return true;
        if(item.storeSetList) {
            for(const store of item.storeSetList) {
                if(store.storeId && store.storeId.indexOf(searchText) >= 0) return true;
                if(store.storeName && store.storeName.indexOf(searchText) >= 0) {
                    return true;
                }
            }
        }
        return false;
    }
    return true;
}

/**
 * (AUTO_PLAN_BIND_GROUP) 정보 View Controller
 * 동적 점포 등록.
 * 메뉴: 자동배차 > 상온 자동배차 기초 정보 > 동적 점포 등록
 * Using React with MUI5
 * Project: NewTOP for cloud
 * 2024-10-15 15:35:56 using DbdbTap
 */
export default function AutoPlanBindGroup({
    viewServiceTags, // for switching view
    show = true,
}) {
    const authTableTree = useReactiveVar(authTableTreeRepo);
    const [editorState, setEditorState] = useState(AppObject.EmptyViewerState);
    const autoPlanBindGroupListData = useReactiveVar(autoPlanBindGroupListRepo);
    const [searchText, setSearchText] = useState('');
    const openConfirmDialog = useConfirmDialog();
    const checkAuth = useAuthorizationCheck();
    const addAlert = useAlertSetter();
    const { enqueueSnackbar } = useSnackbar();
    const lastReloadRef = useRef(0);
    const isEditingRef = useRef(false); // 플래그 생성

    // ##### Call GraphQL to get List #####
    const [getAutoPlanBindStoreSetList, responseList] = useLazyQuery(LIST_AUTO_PLAN_BIND_STORE_SET, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetList(data, option)},
		onError: (error) => addAlert({title: AutoPlanBindGroupDictionary.errorTitle.Get, message: [error.message]})
    });
    const [getAutoPlanBindStoreSetListByGroupKey, responseListByGroupKey] = useLazyQuery(LIST_AUTO_PLAN_BIND_STORE_SET, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetListByGroupKey(data, option)},
		onError: (error) => addAlert({title: AutoPlanBindGroupDictionary.errorTitle.Get, message: [error.message]})
    });

    // ##### GraphQL Mutation. 'onError' for prevent from printing error at console (Uncaught exception) ###
    const [addAutoPlanBindGroup, responseAdd] = useMutation( ADD_AUTO_PLAN_BIND_GROUP, {
		onCompleted: (data, option) => onCompleteAdd(data, option), 
		onError: (error) => addAlert({title: AutoPlanBindGroupDictionary.errorTitle.Add, message: [error.message]})
	} );
    const [editAutoPlanBindGroup, responseEdit] = useMutation( EDIT_AUTO_PLAN_BIND_GROUP, {
		onCompleted: (data, option) => onCompleteEdit(data, option),
		onError: (error) => addAlert({title: AutoPlanBindGroupDictionary.errorTitle.Edit, message: [error.message]})
	} );
    const [removeAutoPlanBindGroup, responseRemove] = useMutation( REMOVE_AUTO_PLAN_BIND_GROUP, {
		onCompleted: (data, option) => onCompleteRemove(data, option),
		onError: (error) => addAlert({title: AutoPlanBindGroupDictionary.errorTitle.Remove, message: [error.message]})
	});

    const getAutoPlanBindStoreSetListNow = () => {
        getAutoPlanBindStoreSetList();
        lastReloadRef.current = new Date().getTime()/1000;
    };

    useEffect(() => {
        const diff = new Date().getTime()/1000 - autoPlanBindGroupListData.when;
        if(diff > 300) //  || autoPlanBindGroupListData.records.length === 0
            getAutoPlanBindStoreSetListNow();
    }, []);

    // >>>>>>>>> callbacks <<<<<<<<<<<<<
    // 센터에 대한 전체 목록 조회.
    const onCompleteGetList = (data, clientOption) => {
        if(data.autoPlanBindStoreSetList) {
            autoPlanBindGroupListRepo({
                when:new Date().getTime()/1000,
                records:makeAutoPlanBindGroupSet(data.autoPlanBindStoreSetList)
            });
        }
    };

    // 특정 그룹에 대한 목록 조회.
    const onCompleteGetListByGroupKey = (data, clientOption) => {
        if(data.autoPlanBindStoreSetList) {
            if(data.autoPlanBindStoreSetList.length > 0) {
                const groupArray = makeAutoPlanBindGroupSet(data.autoPlanBindStoreSetList);
                if(groupArray.length > 0) {
                    setEditorState({open:true, item:groupArray[0]});
                    return;
                }
            }
            addAlert({
                title: AutoPlanBindGroupDictionary.errorTitle.Get,
                message: ["해당 그룹에 속한 동적점포 정보가 없습니다."]
            });
        }
        else addAlert({title: AutoPlanBindGroupDictionary.errorTitle.Get, message: ["해당 그룹에 속한 동적점포 정보를 조회중에 오류가 발생했습니다."]});
    };

    const onCompleteAdd = (data, clientOption) => {
        if(data.autoPlanBindGroupAdd.ok) {
            setEditorState(AppObject.EmptyViewerState);
            getAutoPlanBindStoreSetListNow();
        }
        else addAlert({title:AutoPlanBindGroupDictionary.errorTitle.Add, message:[data.autoPlanBindGroupAdd.message]});
    };

    const onCompleteEdit = (data, clientOption) => {
        if(data.autoPlanBindGroupEdit.ok) {
            setEditorState(AppObject.EmptyViewerState);
            getAutoPlanBindStoreSetListNow();
        }
        else addAlert({title:AutoPlanBindGroupDictionary.errorTitle.Edit, message:[data.autoPlanBindGroupEdit.message]});
    };

    const onCompleteRemove = (data, clientOption) => {
        if(data.autoPlanBindGroupRemove.ok) getAutoPlanBindStoreSetListNow();
        else addAlert({title:AutoPlanBindGroupDictionary.errorTitle.Remove, message:[data.autoPlanBindGroupRemove.message]});
    };

    // 자체 검색 필터. stores에서 storeName 검색을 위해 사용.
    /* const filterItem = (item, search) => {
        if(search) {
            const searchText = search.trim();
            if(searchText.length===0) return true;

            if(item.vehTypeName.indexOf(searchText) >= 0) return true;

            for(const storeItem of item.storeSetList) {
                if(storeItem.storeName && storeItem.storeName.indexOf(searchText) >= 0) {
                    return true;
                }
            }
            return false;
        }
        else return true;
    }; */

    // >>>>>>>>> Event Handler <<<<<<<<<<<<<

    const onClickReload = () => {
        const diff = new Date().getTime()/1000 - lastReloadRef.current;
        if(diff < 3) {
            // Too short to reload
            enqueueSnackbar(AppWord.ReloadNotIn3Sec, {variant:'info', autoHideDuration:2000});
        }
        else getAutoPlanBindStoreSetListNow();
    };

    const onRequestSave = (item, isEdit) => {
        const mutateParam = {variables:{autoPlanBindGroup:item}};
        if(isEdit) editAutoPlanBindGroup(mutateParam);
        else addAutoPlanBindGroup(mutateParam);
    };

    const onClickAdd = () => {
        setEditorState({open:true, item:null});
    };

    const onRequestEdit = (item) => {
        isEditingRef.current = true;
        getAutoPlanBindStoreSetListByGroupKey({variables:{groupOrderNum:item.groupOrderNum}});
    };

    const confirmRemove = async (item) => {
        const confirm = await openConfirmDialog({
            title: "동적점포설정 삭제",
            content: [
                '해당 동적점포 설정을 삭제합니다.',
                '삭제 후 복구할 수 없습니다. 계속하시겠습니까?'
            ]
        });

        if(confirm)
            removeAutoPlanBindGroup({variables:{autoPlanBindGroup:{groupOrderNum:item.groupOrderNum, dcCd:item.dcCd}}});
    };

    const onChangeSearchText = (text) => {
        setSearchText(text);
    };


    const onClickToolOnRow = (toolId, row) => {
        switch(toolId) {
            case AppObject.command.DELETE: confirmRemove(row); break;
            case AppObject.command.EDIT: onRequestEdit(row); break;
            default: return;
        }
    };

    const canAdd = checkAuth(authTableTree.tree.pr_auto_plan_bind_group_add);
    const canEdit = checkAuth(authTableTree.tree.pr_auto_plan_bind_group_edit);
    const canRemove = checkAuth(authTableTree.tree.pr_auto_plan_bind_group_remove);
    const tools = canEdit ? [
        {id:AppObject.command.EDIT, icon:<EditIcon/>},
        //{id:AppObject.command.DELETE, icon:<DeleteForeverIcon/>, color: AppStyle.mui.warning},
    ] : [];
    if(canRemove) tools.push({id:AppObject.command.DELETE, icon:<DeleteForeverIcon/>, color: AppStyle.mui.error});

    const loadingAny
    = responseEdit.loading ||
    responseAdd.loading ||
    responseRemove.loading ||
    responseList.loading ||
    responseListByGroupKey.loading;

    const rootStyle = show ? undefined : {display:'none', visibility:'hidden', pointerEvents:'none'};

    return(
        <ContentConainer style={rootStyle}>
            {viewServiceTags}
            <ContentHeader>
                <ControlBox>
                    <SearchBox
                        value={searchText}
                        onChangeText={onChangeSearchText}
                    />
                </ControlBox>
                <ToolBox>
                    {
                        loadingAny ?
                        <CircularProgress size={20} />
                        :
                        <IconButton
                            color={AppStyle.mui.inherit}
                            onClick={onClickReload}
                        >
                            <CachedIcon />
                        </IconButton>
                    }
                    {
                        canAdd &&
                        <IconButton
                            color={AppStyle.mui.primary}
                            style={{marginLeft:10}}
                            onClick={onClickAdd}
                        >
                            <NoteAddIcon />
                        </IconButton>
                    }
                </ToolBox>
            </ContentHeader>
            <FlexyTable
                name={AutoPlanBindGroupDictionary.tableProps.tableId}
                tableProps={AutoPlanBindGroupDictionary.tableProps}
                records={autoPlanBindGroupListData.records}
                search={searchText}
                filterFunction={filterBindGroup}
                tools={tools}
                onClickTool={onClickToolOnRow}
            />
            <AutoPlanBindGroupInput
                open={editorState.open}
                item={editorState.item}
                onClose={()=>setEditorState(AppObject.EmptyViewerState)}
                responseSaving={editorState.item ? responseEdit : responseAdd}
                onRequestSave={onRequestSave}
            />
        </ContentConainer>
    );
}
